import React, {ReactNode} from 'react';
import {t} from '@lingui/core/macro';

import {findDelaySetting, IDelaySetting} from 'features/http/lib/notificationSettingsView';
import {IHttpMonitorItem} from 'features/http/model/HttpMonitor/IHttpMonitor';
import useNotificationSettings from 'features/http/model/useNotificationSettings';
import {isTranslate} from 'shared/lib/lang';

import styles from './NotificationSettings.module.scss';

interface INotificationSettingsProps {
    item: IHttpMonitorItem;
}

const NotificationSettings: React.FC<INotificationSettingsProps> = ({item}) => {
    const {email, getDelayLabel} = useNotificationSettings();

    const formatDelaySuffix = (delay?: number) => {
        if (!delay) {
            return '';
        }

        return ` ${t`с задержкой`} ${getDelayLabel(delay)}`;
    };

    const renderChannelList = <T extends {id: number}>(
        items: T[],
        settings: IDelaySetting[],
        renderLabel: (_channelItem: T) => ReactNode,
    ) => {
        if (items.length <= 0) {
            return `${t`Отключено`}`;
        }

        return items.map((channelItem) => {
            const setting = findDelaySetting(settings, channelItem.id);

            return (
                <p key={channelItem.id}>
                    {renderLabel(channelItem)}{formatDelaySuffix(setting?.delay)}
                </p>
            );
        });
    };

    const renderEmailNotifications = () => {
        if (!item.notify_by_email) return `${t`Отключено`}`;

        return `${email}${formatDelaySuffix(item.notify_email_confirmed_delay)}`;
    };

    return (
        <div className={styles.notifications}>
            <span>{t`Уведомления на подтвержденный Email`}: {renderEmailNotifications()}</span>
            <span>
                {t`Уведомления на дополнительные Email`}: {
                    renderChannelList(
                        item.additional_emails,
                        item.notify_additional_emails_settings,
                        email => email.email,
                    )
                }
            </span>
            <span>
                {t`Уведомления телеграмм`}: {
                    renderChannelList(
                        item.telegram_chats,
                        item.notify_telegram_chats_settings,
                        chat => <>{t`Уведомление на`} {chat.description}</>,
                    )
                }
            </span>
            {
                !isTranslate() && <span>
                    Уведомления max: {
                        renderChannelList(
                            item.max_chats,
                            item.notify_max_chats_settings,
                            chat => <>{t`Уведомление на`} {chat.description}</>,
                        )
                    }
                </span>
            }
            <span>
                {t`Уведомления вебхуки`}: {
                    renderChannelList(
                        item.webhooks,
                        item.notify_webhooks_settings,
                        webhook => <>{t`Уведомление на`} {webhook.description ?? webhook.url}</>,
                    )
                }
            </span>
        </div>
    );
};

export default NotificationSettings;
